import type { ReactNode } from 'react';

import { Box } from '#/components/Box/Box';
import { Divider } from '#/components/Divider/Divider';
import type { FormUsageContext } from '#/components/Form/Form';
import { Form } from '#/components/Form/Form';
import type {
  FormContextValue,
  FormSubmitFunction,
} from '#/components/Form/FormContext';
import { GeneralError } from '#/components/Form/GeneralError';
import { InputField } from '#/components/Form/InputField';
import { RecaptchaField } from '#/components/Form/RecaptchaField';
import { SubmitButton } from '#/components/Form/SubmitButton/SubmitButton';
import { Hyperlink } from '#/components/Hyperlink/Hyperlink';
import { StyledNativeLinkWithRef } from '#/components/Link/StyledNativeLinkWithRef';
import { AppleOAuthButton } from '#/components/OAuthButton/AppleOAuthButton';
import { FacebookOAuthButton } from '#/components/OAuthButton/FacebookOAuthButton';
import { GoogleOAuthButton } from '#/components/OAuthButton/GoogleOAuthButton';
import { BodySmall } from '#/components/Text/BodySmall';
import { useI18n } from '#/i18n/hooks/useI18n';
import { trackClicked } from '#/utils/analytics/track/trackClicked';

import type { EmailSignupFormValues } from './types/EmailSignupFormValues';

type Props = {
  analyticsTitle: string;
  usageContext: FormUsageContext;
  initialValues: Partial<EmailSignupFormValues>;
  showLoginModal: (email: string | undefined) => void;
  getThirdPartyLoginUrl: (
    arg0: 'facebook' | 'google' | 'apple',
  ) => Promise<string> | string;
  loginOverrideFn?: () => void;
  onSubmit: FormSubmitFunction<EmailSignupFormValues>;
  compactView?: boolean;
  hideLoginBlock?: boolean;
  customButtonText?: string;
  requirePassword?: boolean;
};

type EmailSignupLoginLinkProps = {
  children: ReactNode;
  email?: string;
  showLoginModal: (email: string | undefined) => void;
  loginOverrideFn?: () => void;
};

function EmailSignupLoginLink({
  children,
  email,
  showLoginModal,
  loginOverrideFn,
}: EmailSignupLoginLinkProps) {
  if (loginOverrideFn) {
    return (
      <StyledNativeLinkWithRef
        href="#"
        rel="nofollow"
        qaId="email-form-login-link"
        onClick={(e) => {
          e.preventDefault();
          trackClicked('Log In');
          loginOverrideFn();
        }}
      >
        {children}
      </StyledNativeLinkWithRef>
    );
  }

  return (
    <Hyperlink
      type="button"
      qaId="email-form-login-link"
      clickActions={{
        track: { name: 'Log In' },
      }}
      onClick={() => {
        showLoginModal(email);
      }}
    >
      {children}
    </Hyperlink>
  );
}

export function EmailSignupForm({
  analyticsTitle,
  usageContext,
  initialValues,
  showLoginModal,
  getThirdPartyLoginUrl,
  loginOverrideFn,
  onSubmit,
  compactView,
  hideLoginBlock,
  customButtonText,
  requirePassword,
}: Props) {
  const { i18n } = useI18n();

  return (
    <Form<EmailSignupFormValues, FormContextValue<EmailSignupFormValues>>
      analyticsTitle={analyticsTitle}
      usageContext={usageContext}
      qaId="signup-email-form"
      initialValues={initialValues}
      onSubmit={onSubmit}
      render={({ values: { email } }) => (
        <>
          {!hideLoginBlock && (
            <BodySmall mb={[16, null, 16]} textAlign="center">
              {i18n('Already have an account?')}{' '}
              <EmailSignupLoginLink
                email={email}
                showLoginModal={showLoginModal}
                loginOverrideFn={loginOverrideFn}
              >
                {i18n('Log in')}
              </EmailSignupLoginLink>
            </BodySmall>
          )}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
              marginBottom: 30,
            }}
          >
            <GoogleOAuthButton getUrl={() => getThirdPartyLoginUrl('google')} />
            <FacebookOAuthButton
              getUrl={() => getThirdPartyLoginUrl('facebook')}
            />
            <AppleOAuthButton getUrl={() => getThirdPartyLoginUrl('apple')} />
          </div>

          {!compactView && <Divider marginTop={24} marginBottom={24} />}

          <GeneralError />

          <InputField
            name="email"
            label={i18n('Or use your email:')}
            type="email"
            placeholder={i18n('casey.smith@example.com')}
          />

          {requirePassword && (
            <InputField
              name="password"
              type="password"
              label={i18n('Password')}
              placeholder={i18n('Password')}
            />
          )}

          {(!compactView || email) && <RecaptchaField />}

          <Box mt={[16, null, 40]} display="flex" justifyContent="flex-end">
            <SubmitButton fullWidth size="large">
              {customButtonText ?? i18n('Sign Up')}
            </SubmitButton>
          </Box>
        </>
      )}
    />
  );
}
